import React, { useEffect, useMemo, useState } from 'react';
import { Select, DatePicker, Tag, Input, Button, Pagination } from 'antd';
import { ReloadOutlined, LineChartOutlined } from '@ant-design/icons';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import config from '../../config';
import { NdoePage, NdoePageHeader, NdoeLoading, NdoeError, NdoeResponsiveTable, NdoeDataCard } from '../../components/ndoe';

const DOMAIN = config.REACT_APP_SERVER_DOMAIN;
const { RangePicker } = DatePicker;

// Journal des modifications (admin) : historique tracé (delivery_event), filtrable,
// paginé côté serveur. Lecture seule.
const JournalModifications = () => {
  const role = useSelector((s) => s.user?.currentUser?.role);
  const isAdmin = role === 'admin';
  const navigate = useNavigate();

  const [feed, setFeed] = useState(null);
  const [filtres, setFiltres] = useState({ actions: [], entities: [], auteurs: [] });
  const [page, setPage] = useState(1);
  const [range, setRange] = useState(null);
  const [action, setAction] = useState(undefined);
  const [entity, setEntity] = useState(undefined);
  const [auteur, setAuteur] = useState(undefined);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [erreur, setErreur] = useState(null);

  useEffect(() => {
    axios.get(`${DOMAIN}/api/audit/modifications/filtres`)
      .then(({ data }) => setFiltres(data || { actions: [], entities: [], auteurs: [] }))
      .catch(() => { /* filtres optionnels */ });
  }, []);

  const charger = async () => {
    setLoading(true); setErreur(null);
    try {
      const params = { page: String(page), pageSize: '25' };
      if (q.trim()) params.q = q.trim();
      if (action) params.action = action;
      if (entity) params.entity = entity;
      if (auteur) params.id_user = auteur;
      if (range?.[0]) params.date_debut = range[0].format('YYYY-MM-DD');
      if (range?.[1]) params.date_fin = range[1].add(1, 'day').format('YYYY-MM-DD');
      const { data } = await axios.get(`${DOMAIN}/api/audit/modifications`, { params });
      setFeed(data);
    } catch (e) {
      setErreur(e.response?.data?.error || e.message);
    } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { charger(); }, [page, range, action, entity, auteur, q]);
  const resetPage = (fn) => (v) => { setPage(1); fn(v); };

  const money = (n) => `${Number(n || 0).toLocaleString('fr-FR')} $`;
  const codeCmd = (id) => (id ? `CMD-${String(id).padStart(5, '0')}` : '—');
  const actionTag = (l) => <Tag color={l.sensible ? 'volcano' : 'blue'}>{l.action_label}</Tag>;

  const columns = useMemo(() => [
    { title: 'Date', dataIndex: 'date_event', width: 140, render: (d) => (d ? format(new Date(d), 'dd/MM/yyyy HH:mm') : '—') },
    { title: 'Auteur', dataIndex: 'auteur', render: (a) => a || '—' },
    { title: 'Action', key: 'action', render: (_, l) => actionTag(l) },
    { title: 'Entité', dataIndex: 'entity_label', render: (e) => <Tag>{e}</Tag> },
    { title: 'Référence', key: 'ref', render: (_, l) => (
      <span>{l.id_commande ? <span className="cc-code">{codeCmd(l.id_commande)}</span> : `${l.entity_label} #${l.entity_id}`}{l.client ? ` · ${l.client}` : ''}</span>
    ) },
    { title: 'Détail', dataIndex: 'apercu' },
    { title: 'Motif', dataIndex: 'motif', render: (m) => m || '—' },
  ], []);

  const renderCard = (l) => (
    <NdoeDataCard
      title={l.action_label}
      subtitle={l.date_event ? format(new Date(l.date_event), 'dd/MM/yyyy HH:mm') : undefined}
      badge={actionTag(l)}
      metrics={[
        { label: 'Auteur', value: l.auteur || '—' },
        { label: 'Réf.', value: l.id_commande ? codeCmd(l.id_commande) : `#${l.entity_id}` },
      ]}
    >
      <div style={{ fontSize: 13, color: 'var(--ndoe-muted)' }}>{l.apercu}{l.motif ? ` · ${l.motif}` : ''}</div>
    </NdoeDataCard>
  );

  if (!isAdmin) return <NdoePage><NdoeError text="Accès réservé à l’administrateur." /></NdoePage>;

  return (
    <NdoePage>
      <NdoePageHeader
        eyebrow="Administration"
        title="Journal des modifications"
        subtitle="Historique tracé des changements sensibles (régularisations, prix, paiements, remboursements, affectations…) — filtrable et paginé."
      />

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', margin: '10px 0 16px' }}>
        <RangePicker value={range} onChange={resetPage(setRange)} format="DD/MM/YYYY" allowEmpty={[true, true]} />
        <Select allowClear showSearch optionFilterProp="label" placeholder="Type d’action" style={{ minWidth: 200 }}
          value={action} onChange={resetPage(setAction)}
          options={filtres.actions.map((a) => ({ value: a.value, label: `${a.label} (${a.n})` }))} />
        <Select allowClear placeholder="Entité" style={{ minWidth: 140 }}
          value={entity} onChange={resetPage(setEntity)}
          options={filtres.entities.map((e) => ({ value: e.value, label: e.label }))} />
        <Select allowClear showSearch optionFilterProp="label" placeholder="Auteur" style={{ minWidth: 160 }}
          value={auteur} onChange={resetPage(setAuteur)}
          options={filtres.auteurs.map((a) => ({ value: a.value, label: `${a.label} (${a.n})` }))} />
        <Input.Search placeholder="Rechercher (auteur, motif, cliente…)" allowClear style={{ maxWidth: 260 }}
          onSearch={resetPage(setQ)} />
        <Button icon={<ReloadOutlined />} onClick={charger}>Actualiser</Button>
        <Button icon={<LineChartOutlined />} onClick={() => navigate('/rapports/modifications')}>Analyse</Button>
      </div>

      {loading ? <NdoeLoading rows={8} />
        : erreur ? <NdoeError text={erreur} onRetry={charger} />
          : (
            <NdoeResponsiveTable
              rowKey="id_event"
              columns={columns}
              data={feed?.lignes || []}
              renderCard={renderCard}
              pagination={{ current: page, pageSize: feed?.pageSize || 25, total: feed?.total || 0, showSizeChanger: false, onChange: setPage }}
              mobilePager={(feed?.total || 0) > (feed?.pageSize || 25) ? (
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: 12 }}>
                  <Pagination simple current={page} pageSize={feed?.pageSize || 25} total={feed?.total || 0} onChange={setPage} />
                </div>
              ) : null}
              scroll={{ x: 980 }}
              empty={{ icon: '🗂️', title: 'Aucune modification', text: 'Rien à afficher pour ces filtres.' }}
            />
          )}
    </NdoePage>
  );
};

export default JournalModifications;
